import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';

interface ThreadModalProps {
  // Close the modal (called AFTER the collapse animation so the parent keeps us
  // mounted long enough to animate out).
  onClose: () => void;
  // The thread HEADER, rendered as the modal's header so it matches the card.
  // It's a render-prop taking the modal's animated `close` so the header's own
  // "Hide Details" / subject click can trigger the collapse animation (same close
  // path as ESC / backdrop). The card passes its open handler instead.
  renderHeader: (close: () => void) => React.ReactNode;
  // Screen rect of the thread card/header that was clicked — the header COPY
  // flies from here to the top-center, then the body expands beneath it.
  originRect?: DOMRect | { top: number; left: number; width: number; height: number } | null;
  // The thread's ROOT id (messages.id). Used so the floating AI Assist launcher
  // opens a chat attached to THIS thread.
  threadRef?: number | null;
  // Optional shimmer class ('sl-working-sally' | 'sl-working-human' | '') applied
  // to the modal panel so the WHOLE modal shimmers while Sally / another teammate
  // is working the thread.
  workingClass?: string;
  children: React.ReactNode;
}

// Two-phase "grow out of the thread" overlay (Michael's spec):
//   PHASE 1 — a copy of the thread header flies from the card's on-screen rect to
//             the top-center resting position (the body is collapsed, so only the
//             header is visible and it looks like just the header moving).
//   PHASE 2 — once the header lands, the body expands to full height beneath it.
// Close reverses it: the body collapses, then the header flies back into the card.
// Rendered in a portal on document.body so a background feed re-sort can't move or
// close it. Respects prefers-reduced-motion (opens/closes instantly).
const _FLY_MS = 300;    // header travel
const _EXPAND_MS = 260; // body reveal

const _reduce = (): boolean => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
  catch { return false; }
};

const ThreadModal: React.FC<ThreadModalProps> = ({ onClose, renderHeader, originRect, threadRef, workingClass, children }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyInnerRef = useRef<HTMLDivElement>(null);
  const reduce = _reduce();

  // landed = header has reached the top-center (phase 1 done).
  // expanded = body is open (phase 2 done).
  const [landed, setLanded] = useState(reduce);
  const [expanded, setExpanded] = useState(reduce);
  const [bodyMax, setBodyMax] = useState(0);
  // Whether the AI Assist panel is open (broadcast from App via a window event).
  // When open, the modal shrinks to the region LEFT of the panel so the two sit
  // side by side; when closed, a floating launcher lets you open it beside the modal.
  const [assistOpen, setAssistOpen] = useState<boolean>(() => !!(window as any).__slAssistOpen);
  // sm+ breakpoint (640px). Below it the assist panel is full-width, so we don't
  // reserve side-by-side space (the modal stays full-bleed).
  const [wide, setWide] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth >= 640 : true));
  // On-screen bottom/right of the MODAL's status dropdown. The floating AI Assist
  // launcher is tucked directly beneath it (right edges aligned) — one placement
  // for every screen size, so the button always sits in the same spot relative to
  // the modal rather than chasing the header toggle's arbitrary position.
  const [statusRect, setStatusRect] = useState<{ bottom: number; right: number } | null>(null);
  const closingRef = useRef(false);
  const timers = useRef<number[]>([]);
  const addTimer = (id: number) => { timers.current.push(id); };

  // Transform that maps the panel's RESTING (header-only) rect onto the origin
  // card rect — top-left anchored. Used while the header is "flying".
  const flyTransform = useCallback((): string => {
    const el = panelRef.current;
    if (!el || !originRect) return 'translateY(-8px) scale(0.98)';
    const f = el.getBoundingClientRect();
    if (!f.width || !f.height) return 'translateY(-8px) scale(0.98)';
    const sx = Math.max(0.05, originRect.width / f.width);
    const sy = Math.max(0.05, originRect.height / f.height);
    const tx = originRect.left - f.left;
    const ty = originRect.top - f.top;
    return `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
  }, [originRect]);

  // FIXED body height = all remaining viewport space below the landed header
  // (Michael's spec: the modal always takes its max possible height, even for a
  // small thread — so this is a height, not a content-hugging maxHeight).
  // bottomGap mirrors the overlay's p-3 sm:p-6 padding (Tailwind sm = 640px).
  const measureBody = useCallback(() => {
    const header = headerRef.current;
    const bottomGap = window.innerWidth >= 640 ? 24 : 12;
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    setBodyMax(Math.max(160, Math.round(window.innerHeight - headerBottom - bottomGap)));
  }, []);

  // ENTER: park header on the origin (before paint), fly it up, then expand body.
  useLayoutEffect(() => {
    if (reduce) { measureBody(); return; }
    // Capture the timers array (a stable ref, never reassigned) so the cleanup
    // clears the same list addTimer pushes into — not a possibly-changed .current.
    const timerList = timers.current;
    const el = panelRef.current;
    if (el) { el.style.transformOrigin = 'top left'; el.style.transform = flyTransform(); }
    // next frame: release to top-center (phase 1)
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setLanded(true)));
    // after the fly completes: measure + expand the body (phase 2)
    addTimer(window.setTimeout(() => { measureBody(); setExpanded(true); }, _FLY_MS + 20));
    return () => {
      cancelAnimationFrame(raf);
      timerList.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CLOSE: collapse body, then fly the header back, then unmount.
  const requestClose = useCallback(() => {
    if (closingRef.current) { return; }
    closingRef.current = true;
    if (reduce) { onClose(); return; }
    setExpanded(false);                                   // phase 2 reverse
    addTimer(window.setTimeout(() => setLanded(false), _EXPAND_MS));   // phase 1 reverse
    addTimer(window.setTimeout(onClose, _EXPAND_MS + _FLY_MS));        // unmount
  }, [onClose, reduce]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // NOTE: we deliberately do NOT set body { overflow: hidden } here. The app uses
  // the window as its scroll container so the page header (sticky top-0) and the
  // feed's filter bar (sticky top-16) can pin. Locking body overflow collapses
  // that sticky context — the header would scroll away while the modal is open —
  // without actually preventing the background from scrolling. So we leave the
  // background scrollable and let the header stay pinned beneath the dimmed
  // overlay (Michael's ask: the header must stay at the top with a modal open).

  // Keep the fixed body height correct across viewport resizes (and track the
  // sm+ breakpoint for the side-by-side layout).
  useEffect(() => {
    const onResize = () => { measureBody(); setWide(window.innerWidth >= 640); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureBody]);

  // React to the AI Assist panel opening/closing (broadcast by App). The modal
  // slides left to make room, and the floating launcher shows/hides accordingly.
  useEffect(() => {
    const onAssist = (e: Event) => setAssistOpen(!!(e as CustomEvent).detail?.open);
    window.addEventListener('sl-assist-open-changed', onAssist);
    return () => window.removeEventListener('sl-assist-open-changed', onAssist);
  }, []);

  // Track the MODAL status dropdown's on-screen rect so the floating launcher can
  // tuck directly beneath it (right edges aligned). Scoped to panelRef because the
  // feed BEHIND the modal also renders status selects — an unscoped query would grab
  // a feed card's. getBoundingClientRect is affected by the panel's entrance
  // transform (it scales up from the origin card), so a measurement taken mid-fly
  // reports a too-small `right` and the button lands LEFT of the dropdown's edge
  // (not hugging). We therefore also re-measure on the panel's transform
  // transitionend — the authoritative "settled" signal — plus on assist toggle and
  // viewport resize.
  useEffect(() => {
    const measure = () => {
      const el = panelRef.current?.querySelector('[data-thread-status]') as HTMLElement | null;
      if (!el) { setStatusRect(null); return; }
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) { setStatusRect(null); return; }
      setStatusRect({ bottom: r.bottom, right: r.right });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    // Belt-and-suspenders: whichever element is animating (the panel's entrance
    // transform, or the overlay's `right` when the assist panel closes and the
    // modal recenters), everything is settled by _FLY_MS. Re-measure then so the
    // launcher always ends up on the dropdown's true right edge.
    const settle = window.setTimeout(measure, _FLY_MS + 60);
    const panel = panelRef.current;
    const onPanelTransitionEnd = (e: TransitionEvent) => {
      if (e.target === panel && e.propertyName === 'transform') measure();
    };
    panel?.addEventListener('transitionend', onPanelTransitionEnd);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      panel?.removeEventListener('transitionend', onPanelTransitionEnd);
      window.removeEventListener('resize', measure);
    };
  }, [assistOpen, landed, expanded]);

  const panelStyle: React.CSSProperties = reduce ? {} : {
    transformOrigin: 'top left',
    transform: landed ? 'translate(0px,0px) scale(1,1)' : flyTransform(),
    opacity: landed ? 1 : (originRect ? 0.9 : 0),
    transition: `transform ${_FLY_MS}ms cubic-bezier(0.22,1,0.36,1), opacity ${_FLY_MS}ms ease`,
    willChange: 'transform, opacity',
  };
  // height (not maxHeight) so the body ALWAYS fills the remaining viewport,
  // even when the thread's content is short.
  const bodyStyle: React.CSSProperties = reduce ? {
    height: bodyMax || undefined,
    overflowY: 'auto',
  } : {
    height: expanded ? bodyMax : 0,
    opacity: expanded ? 1 : 0,
    overflowY: expanded ? 'auto' : 'hidden',
    transition: `height ${_EXPAND_MS}ms cubic-bezier(0.22,1,0.36,1), opacity ${_EXPAND_MS}ms ease`,
  };

  // When the assist panel is open on a wide screen, reserve its width (30rem) on
  // the right so this overlay (and its centered panel) occupies the LEFT region.
  const assistReserve = assistOpen && wide ? '30rem' : 0;

  return createPortal(
    <>
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-3 sm:p-6"
      onClick={requestClose}
      role="dialog"
      aria-modal="true"
      style={{
        backgroundColor: 'rgba(0,0,0,0.4)',
        right: assistReserve,
        opacity: reduce ? 1 : (landed ? 1 : 0),
        transition: reduce
          ? `right ${_FLY_MS}ms cubic-bezier(0.22,1,0.36,1)`
          : `opacity ${_FLY_MS}ms ease, right ${_FLY_MS}ms cubic-bezier(0.22,1,0.36,1)`,
      }}
    >
      {/* Width mirrors the feed's thread-list container (MessageFeed) so the
          modal lines up with the rows behind it; the overlay shares the feed's
          p-3 sm:p-6 padding, making the percentage bases match. */}
      <div
        ref={panelRef}
        className={"relative w-full sm:w-[92%] lg:w-[88%] xl:w-[82%] 2xl:w-[75%] max-w-[1800px] flex flex-col bg-white rounded-lg shadow-xl overflow-hidden " + (workingClass || '')}
        onClick={(e) => e.stopPropagation()}
        style={panelStyle}
      >
        {/* Header = the SAME block as the thread card (subject + action row). */}
        <div ref={headerRef} className="flex-shrink-0">
          {renderHeader(requestClose)}
        </div>
        {/* Body expands (phase 2) beneath the landed header. */}
        <div className="bg-gray-50" style={bodyStyle}>
          <div ref={bodyInnerRef} className="px-3 py-3">
            {children}
          </div>
        </div>
      </div>
    </div>

    {/* Floating AI Assist launcher — shown only while the panel is CLOSED. Tucked
        directly beneath the modal's status dropdown (right edges aligned), the same
        spot on every screen size. Opening it slides the modal left (above) and
        starts a chat attached to THIS thread. Carries a repeating glimmer sweep. */}
    {!assistOpen && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('sl-open-assist', { detail: { threadRef: threadRef ?? null } }));
        }}
        title="Talk to Sally about this thread"
        style={statusRect
          ? { position: 'fixed', top: statusRect.bottom + 6, right: Math.max(8, window.innerWidth - statusRect.right) }
          : { position: 'fixed', top: 60, right: 24 }}
        className="z-[61] p-2 flex items-center justify-center rounded-lg bg-brand-100 text-brand-700 shadow-xl ring-1 ring-white/60 hover:bg-brand-200 active:scale-95 transition-colors overflow-hidden"
      >
        <span className="pointer-events-none absolute inset-0 rounded-lg overflow-hidden">
          <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/70 to-transparent animate-glimmer" />
        </span>
        <Sparkles className="relative w-5 h-5" />
      </button>
    )}
    </>,
    document.body
  );
};

export default ThreadModal;
